<template>
  <div>
      <b-row>
    <b-col cols="12"  class="my-1">
        <div class="d-flex align-items-center justify-content-end">
          <b-button v-if="HasPermission(PermissionList.Sales_Create)" variant="primary" to="/leads/leads-add">
            <span class="text-nowrap">Add Lead</span>
          </b-button>
        </div>
      </b-col>

    </b-row>

      <b-row>
    <b-col cols="12" md="6" class="my-1">
      <b-form-group label="Select Columns:" class="mb-0">
        <b-form-checkbox-group 
          v-model="selectedColumns" 
          :options="columnOptions"
        ></b-form-checkbox-group>
      </b-form-group>
    </b-col>
  </b-row>

  <b-col  cols="12" sm="2" class="my-1">
        <b-form-group class="mb-0">
          <label class="d-inline-block text-sm-left mr-50">Per page</label>
          <b-form-select id="perPageSelect" v-model="perPage" size="sm" :options="pageOptions"  />
        </b-form-group>
      </b-col>

      <b-col cols="12"  class="my-1">
        <div class="d-flex align-items-center justify-content-end">
          <b-button v-if="HasPermission(PermissionList.Sales_Search)" variant="outline-danger"  @click="clearFilters">
            Clear Filters
          </b-button>
        </div>
      </b-col>
    
  <b-row>
      <b-col cols="12">
      
        <b-table striped hover responsive :per-page="perPage" :current-page="currentPage" :items="filteredLeads"
          :fields="dynamicFields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :sort-direction="sortDirection"
          @filtered="onFiltered">
          <template #head(id)>
            <div class="d-flex flex-column">
              <span>Lead Name</span>
              <b-form-input v-model="filters.leadName" placeholder="Filter by Lead Name" size="sm"
                @input="filterTable" />
            </div>
          </template>

          <template #head(typeDesc)>
            <div class="d-flex flex-column">
              <span>Lead Type</span>
              <b-form-select v-model="filters.leadType" :options="leadTypeOptions" size="sm" @change="filterTable" />
            </div>
          </template>

          <template #head(email)>
            <div class="d-flex flex-column">
              <span>Email</span>
              <b-form-input v-model="filters.email" placeholder="Filter by Email" size="sm" @input="filterTable" />
            </div>
          </template>

          <template #head(salesPersonId)>
            <div class="d-flex flex-column">
              <span>Sales Person</span>
              <b-form-input v-model="filters.salesPerson" placeholder="Filter by Sales Person" size="sm"
                @input="filterTable" />
            </div>
          </template>

          <template #head(stage)>
            <div class="d-flex flex-column">
              <span>Status</span>
              <b-form-input v-model="filters.status" placeholder="Filter by Status" size="sm" @input="filterTable" />
            </div>
          </template>

          <template #head(createdOn)>
            <div class="d-flex flex-column">
              <span>Created On</span>
              <DatePicker v-model="filters.createdOnRange" range placeholder="Filter by Date Range" size="sm"
                @change="filterTable" :clearable="false" :input-attr="{ readonly: true }" @focus="openCalendar"
                class="small-datepicker" />
            </div>
          </template>


          <template #cell(status)="data">
            <b-badge v-if="data.item.status === 2 && data.item.isClient" variant="primary">
              Won
            </b-badge>
            <b-badge v-else-if="data.item.status === 3 && data.item.isClient" variant="primary">
              Direct Customer
            </b-badge>
            <b-badge v-else-if="data.item.status === 0" variant="info">
              New
            </b-badge>
            <b-badge v-else-if="data.item.status === 1" variant="Warning">
              Discussion
            </b-badge>
          </template>
          <template #cell(salesPersonId)="data">
            <p v-if="data.item.salesPersonId === null">-</p>
            <p v-else>{{ getSalePersonName(data.item.salesPersonId) }}</p>
          </template>

          <template #cell(id)="data">
            <p v-if="data.item.id === null">-</p>
            <p v-else>{{ getLeadFullName(data.item.id) }}</p>
          </template>

          <template #cell(stage)="data">
            <p v-if="data.item.stage">
              {{ data.item.stage }}
            </p>

            <p v-else>
              -
            </p>


          </template>

          <template #cell(createdOn)="data">

            {{ formatDate(data.item.createdOn) }}
          </template>
          <template #cell(actions)="data">
            <b-button variant="primary" v-if="data.item.isClient" :to="returnToCustomer(data.item.id)">
              <feather-icon icon="Edit2Icon" class="mr-50" />
              <span>View</span>
            </b-button>
            <b-button v-else :to="getLeadEditData(data.item.id)" variant="primary">
              <feather-icon icon="Edit2Icon" class="mr-50" /> <span>View</span>
            </b-button>
          </template>
        </b-table>
        <div class="d-flex justify-content-center mb-1">
          <b-spinner variant="success" label="Spinning" block v-if="loading"
            style="width: 3rem; height: 3rem"></b-spinner>
        </div>
      </b-col>

      <b-col cols="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center" size="sm"
          class="my-0" />
        <b-form-group>
          Showing 
        {{ totalRows > 0 ? (perPage * (currentPage - 1) + 1) : 0 }} 
        to 
        {{ totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0 }} 
        of 
        {{ totalRows > 0 ? totalRows : 0 }} 
        entries
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import {
  BTable,
  BAvatar,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BSpinner,
  BDropdown,
  BDropdownItem,
  BFormCheckboxGroup,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import { PermissionList } from '@/libs/defaultList';
import { HasPermission } from '@/@core/utils/validations/permissionCheck';
import { fnSortingDropdown, customLoader } from "@/libs/customFn";

export default {
  components: {
    BTable,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BSpinner,
    BDropdown,
    BDropdownItem,
    DatePicker,
    BFormCheckboxGroup
  },
  data() {
    return {
      companyName: "",
      email: "",
      otherName: "",
      surname: "",
      typeDesc: "",
      status: "",
      leads: [],
      isActive: false,
      selectedName: null,
      selectedType: null,
      selectedEmail: null,
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 0,
      currentPage: 0,
      sortBy: "",
      filters: {
        leadName: "",
        leadType: "",
        email: "",
        salesPerson: "",
        status: "",
        createdOnRange: null,
      },
      isEdit: false,
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      leadName: [],
      leadEmail: [],
      leadType: [
        { text: "New", value: 0 },
        { text: "Discussion", value: 1 },
        { text: "Won", value: 2 },
        { text: "Direct Customer", value: 3 },
      ],
      leadTypeOptions: [
        { value: "", text: "All" }, // Default "All" option
        { value: "INDIVIDUAL", text: "INDIVIDUAL" },
        { value: "COMPANY", text: "COMPANY" },
      ],
      infoModal: {
        id: "info-modal",
        title: "",
        content: "",
      },
      selectedColumns: ['id', 'typeDesc', 'email', 'salesPersonId', 'createdOn', 'stage', 'actions'], // Default selected columns
      columnOptions: [
        
        { value: 'typeDesc', text: 'Lead Type' },
        { value: 'email', text: 'Email' },
        { value: 'salesPersonId', text: 'Sales Person' },
        { value: 'createdOn', text: 'Created On' },
        { value: 'stage', text: 'Status' },
        
      ],
      // fields to be displayed on table
      fields: [
        { key: "id", label: "Lead Name", sortable: true },

        { key: "typeDesc", label: "Lead Type" },
        { key: "email", label: "Email", sortable: true },
        { key: "salesPersonId", label: "Sales Person" },
        { key: "createdOn", label: "Created On", sortable: "true" },
        { key: "stage", label: "Status", sortable: "true" },
        { key: "actions" },
      ],
    };
  },
  computed: {
    filteredLeads() {
      // Check if the leads array is being set and used correctly
      return this.leads.length > 0 ? this.leads : [];
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter((f) => f.sortable)
        .map((f) => ({ text: f.label, value: f.key }));
    },
    ...mapGetters("leadModule", {
      leadList: "leads",
      loading: "loading",
    }),
    ...mapGetters("salesPersonModule", {
      SalesPersonList: "salesperson",
      loading: "loading",
    }),

    dynamicFields() {
      return this.fields.filter(field => this.selectedColumns.includes(field.key));
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.leadList.length;

  },

  methods: {
    clearFilters() {
      // Reset all filter values to their default (e.g., empty string or null)
      this.filters = {
        leadName: "",
        leadType: "",
        email: "",
        salesPerson: "",
        status: "",
        createdOnRange: null, // Reset the date range filter
      };

      // Reset the leads list to the original unfiltered list
      this.leads = [...this.leadList.filter(lead => !lead.isClient)];

      // Reset pagination to the first page
      this.currentPage = 1;
    },
    orderFilterChanged() {
      this.leads = this.leadList.filter(lead => !lead.isClient);
      if (this.selectedName) {
        this.leads = this.leads.filter((obj) => {
          return obj.id === this.selectedName;
        });
      }
      if (this.selectedType) {
        let selectedTypeNumber = Number(this.selectedType);
        this.leads = this.leads.filter((obj) => {
          return obj.status === selectedTypeNumber;
        });
      }
      if (this.selectedEmail) {
        this.leads = this.leads.filter((obj) => {
          return obj.id === this.selectedEmail;
        });
      }

      this.totalRows = this.leads.length;
      this.currentPage = 1;
    },

    formatDate(date) {
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(date).toLocaleDateString("en-US", options);
    },

    getLeadFullName(id) {
      const leadsFull = this.leadList.find((l) => l.id === id);

      const first = leadsFull.otherName
        ? leadsFull.otherName
        : "";

        const last = leadsFull.surname
        ? leadsFull.surname
        : "";

      return first + " " + last;
    },

    getSalePersonName(id) {
      const salesPerson = this.SalesPersonList.find((s) => s.id === id);
      return salesPerson
        ? salesPerson.firstName + " " + salesPerson.lastName
        : "";
    },
    ...mapActions("salesPersonModule", ["getSalesPersonListAction"]),
    ...mapActions("leadModule", ["getLeadListAction", "removeLeadAction"]),

    returnToCustomer(id) {
      return "/customer/customer-view/" + id;
    },
    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`;
      this.infoModal.content = JSON.stringify(item, null, 2);
      this.$root.$emit("bv::show::modal", this.infoModal.id, button);
    },
    resetInfoModal() {
      this.infoModal.title = "";
      this.infoModal.content = "";
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    getLeadEditData(data) {
      return "/leads/leads-view/" + data;
    },

    removeLead(id) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.removeLeadAction(id);
          this.$swal({
            icon: "success",
            title: "Deleted!",
            text: "Lead has been deleted",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
        } else if (result.dismiss === "cancel") {
          this.$swal({
            title: "Cancelled",
            text: "Lead not deleted:)",
            icon: "error",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
        }
      });
    },
    filterTable() {
      this.leads = this.leadList.filter(lead => !lead.isClient).filter((lead) => {
        const matchesLeadName = this.filters.leadName
          ? lead.otherName && lead.otherName.toLowerCase().includes(this.filters.leadName.toLowerCase())
          : true;

        const matchesLeadType = this.filters.leadType
          ? lead.typeDesc && lead.typeDesc.toUpperCase() === this.filters.leadType
          : true;

        const matchesEmail = this.filters.email
          ? lead.email && lead.email.toLowerCase().includes(this.filters.email.toLowerCase())
          : true;

        const matchesSalesPerson = this.filters.salesPerson
          ? this.getSalePersonName(lead.salesPersonId).toLowerCase().includes(this.filters.salesPerson.toLowerCase())
          : true;

        const matchesStatus = this.filters.status
          ? lead.stage && lead.stage.toLowerCase().includes(this.filters.status.toLowerCase())
          : true;

        const matchesCreatedOn = this.filters.createdOnRange && this.filters.createdOnRange.length === 2
          ? new Date(lead.createdOn) >= new Date(this.filters.createdOnRange[0]) &&
          new Date(lead.createdOn) <= new Date(this.filters.createdOnRange[1])
          : true;

        return matchesLeadName && matchesLeadType && matchesEmail && matchesSalesPerson && matchesStatus && matchesCreatedOn;
      });

      this.totalRows = this.leads.length;
      this.currentPage = 1; // Reset pagination to the first page
    },
    openCalendar(event) {
      event.target.blur();
      event.target.click();

      document.addEventListener('click', this.closeCalendar);
    },
    closeCalendar(event) {
      const datePicker = this.$refs.datePicker.$el;
      if (!datePicker.contains(event.target)) {
        this.$refs.datePicker.hideCalendar();

        document.removeEventListener('click', this.closeCalendar);
      }
    },

    async loaderSwal() {
    try {
      this.$swal({
        title: "Loading...",
        text: "Please wait while we fetch the data...",
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });

      await this.getSalesPersonListAction();
      await this.getLeadListAction();

      this.leads = this.leadList.filter(lead => !lead.isClient);
      this.totalRows = this.leads.length;

      this.leads.forEach(v => {
        this.leadName.push({ value: v.id, text: `${v.otherName}` });
        this.leadEmail.push({ value: v.id, text: `${v.email}` });
      });

      this.leadName = fnSortingDropdown(this.leadName);
      this.leadName.unshift({ value: "", text: "All" });

      this.leadEmail = fnSortingDropdown(this.leadEmail);
      this.leadEmail.unshift({ value: "", text: "All" });

      this.leadEmail.sort((a, b) => {
        const emailA = a.text.toLowerCase();
        const emailB = b.text.toLowerCase();
        if (emailA < emailB) return -1;
        if (emailA > emailB) return 1;
        return 0;
      });

      this.$swal.close();
    } catch (error) {
      this.$swal.close();
      this.$swal({
        title: "Error",
        text: "There was an error loading the data. Please try again later.",
        icon: "error",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
      console.error("Error while loading data:", error);
    }
  },
  },
  async mounted() {
    await this.loaderSwal();
  },
  setup(props) {
    return {
      PermissionList,
      HasPermission
    }
  }
};
</script>
<style scoped>
.small-datepicker {
  width: 150px;
}
</style>
